import { NavigationOptions, PaginationOptions, SwiperOptions } from 'swiper/types';
import Swiper from 'swiper';
import { Navigation, Pagination, Thumbs, Autoplay } from 'swiper/modules';
import initParams from 'store/initParams';

type SliderMode = 'PICTURES' | 'CARDS' | 'THUMBS' | 'SUGGESTIONS';

/**
 * ImageSliderManager - Singleton class for managing Swiper-based image sliders
 *
 * This class provides centralized management for all Swiper instances in the application,
 * ensuring proper memory management, consistent configuration, and cleanup.
 */
export class ImageSliderManager {
  private static instance: ImageSliderManager;
  private swiperInstances: WeakMap<HTMLElement, Swiper[]>;

  private constructor() {
    this.swiperInstances = new WeakMap();
  }

  /**
   * Get the singleton instance of ImageSliderManager
   */
  public static getInstance(): ImageSliderManager {
    if (!ImageSliderManager.instance) {
      // console.debug('[ImageSliderManager] Singleton instance created');
      ImageSliderManager.instance = new ImageSliderManager();
    }
    return ImageSliderManager.instance;
  }

  /**
   * Initialize a single slider with the specified mode and options
   * @param container - The container element that holds the Swiper
   * @param mode - The slider mode (PICTURES, CARDS, THUMBS, SUGGESTIONS)
   * @param forceOptions - Additional options to override defaults
   * @returns The initialized Swiper instance
   */
  public initSlider(container: HTMLElement | null, mode: SliderMode, forceOptions?: SwiperOptions): Swiper {
    if (container === null) {
      throw new Error(`Slider container is null`);
    }

    // console.debug('[ImageSliderManager] Initializing slider', {
    //   mode,
    //   containerId: container.id || 'no-id',
    //   className: container.className,
    // });

    const sliderOptions = this.getSliderOptions(container, mode, forceOptions);

    this.removeHtmlElements(
      container,
      typeof sliderOptions.navigation !== 'undefined' && sliderOptions.navigation !== false,
      typeof sliderOptions.pagination !== 'undefined' && sliderOptions.pagination !== false,
    );

    const swiper = new Swiper(container.querySelector('.swiper') as HTMLElement, {
      modules: [Navigation, Pagination, Thumbs, Autoplay],
      ...sliderOptions,
      on: {
        afterInit: () => {
          container.setAttribute('initialized', 'true');
        },
        fromEdge: this.toggleNavs,
        toEdge: this.toggleNavs,
        beforeInit: this.toggleNavs,
        init: swiper => this.updateCenteredSlides(swiper, mode),
        slideChange: swiper => this.updateCenteredSlides(swiper, mode),
      },
    });

    this.trackInstance(container, swiper);
    // console.debug('[ImageSliderManager] Slider initialized successfully', { mode });
    return swiper;
  }

  /**
   * Initialize two sliders (main and thumbs) and synchronize them
   * @param mainContainer - Container for the main slider
   * @param thumbsContainer - Container for the thumbnails slider
   * @returns Tuple with [thumbs, main] Swiper instances
   */
  public initSliderWithThumbs(mainContainer: HTMLElement | null, thumbsContainer: HTMLElement | null): [Swiper, Swiper] {
    const thumbs = this.initSlider(thumbsContainer, 'THUMBS');

    const main = this.initSlider(mainContainer, 'PICTURES', {
      thumbs: {
        swiper: thumbs,
      },
    });

    return [thumbs, main];
  }

  /**
   * Initialize all sliders within a container
   * @param container - Parent container with multiple .swiper-container elements
   * @param mode - The slider mode for all sliders
   * @param forceOptions - Additional options to override defaults
   */
  public initAllSliders(container: HTMLElement | null, mode: SliderMode, forceOptions?: SwiperOptions): void {
    if (!container) return;

    const swiperContainers = container.querySelectorAll('.swiper-container');
    const uninitializedContainers = Array.from(swiperContainers).filter(container => container.getAttribute('initialized') !== 'true');

    if (uninitializedContainers.length > 0) {
      // console.debug('[ImageSliderManager] Initializing multiple sliders', {
      //   mode,
      //   total: swiperContainers.length,
      //   initializing: uninitializedContainers.length,
      // });
    }

    swiperContainers.forEach(swiperContainer => {
      if (swiperContainer.getAttribute('initialized') !== 'true') {
        this.initSlider(swiperContainer as HTMLElement, mode, forceOptions);
      }
    });
  }

  /**
   * Destroy a specific slider and clean up all associated resources
   * @param container - The container element that holds the Swiper
   */
  public destroySlider(container: HTMLElement | null): void {
    if (!container) return;

    // console.debug('[ImageSliderManager] Destroying slider', {
    //   containerId: container.id || 'no-id',
    //   className: container.className,
    // });

    const instances = this.swiperInstances.get(container);
    if (instances) {
      // console.debug('[ImageSliderManager] Found instances to destroy', { count: instances.length });
      instances.forEach(swiper => {
        if (swiper && !swiper.destroyed) {
          swiper.destroy(true, true); // Remove all event listeners and free memory
        }
      });
      this.swiperInstances.delete(container);
    } else {
      // console.debug('[ImageSliderManager] No instances found to destroy');
    }

    // Clean up DOM elements
    container.querySelector('.swiper-button-prev-custom')?.remove();
    container.querySelector('.swiper-button-next-custom')?.remove();
    container.querySelector('.swiper-pagination-custom')?.remove();

    // Reset initialization flag
    container.removeAttribute('initialized');

    // Remove slider-specific classes
    container.classList.remove('pictures-carousel', 'cards-carousel', 'thumbs-carousel', 'suggestions-carousel');
  }

  /**
   * Destroy all sliders within a container and clean up all associated resources
   * @param container - The parent container that holds multiple sliders
   */
  public destroyAllSliders(container: HTMLElement | null): void {
    if (!container) return;

    container.querySelectorAll('.swiper-container').forEach(swiperContainer => {
      this.destroySlider(swiperContainer as HTMLElement);
    });
  }

  /**
   * Reinitialize all sliders within a container (destroys existing ones first)
   * @param container - The parent container
   * @param mode - The slider mode for all sliders
   * @param forceOptions - Additional options to override defaults
   */
  public reinitAllSliders(container: HTMLElement | null, mode: SliderMode, forceOptions?: SwiperOptions): void {
    this.destroyAllSliders(container);
    this.initAllSliders(container, mode, forceOptions);
  }

  // Private helper methods

  // Creates navigation button configuration for sliders
  private getNavigationOptions(nextButton: HTMLElement, prevButton: HTMLElement): NavigationOptions {
    return {
      nextEl: nextButton,
      prevEl: prevButton,
      enabled: !initParams.isMobile,
    };
  }

  // Creates pagination dots configuration for sliders
  private getPaginationOptions(elPagination: HTMLElement): PaginationOptions {
    return {
      el: elPagination,
      enabled: true,
      clickable: true,
      bulletActiveClass: '-active',
      bulletClass: 'nav-dot',
      bulletElement: 'li',
      dynamicBullets: true,
      type: 'bullets',
    };
  }

  // Builds complete Swiper configuration based on mode and options
  private getSliderOptions(container: HTMLElement, mode: SliderMode, forceOptions?: SwiperOptions): SwiperOptions {
    this.addHtmlElements(container, mode);
    const prevButton = container.querySelector('.swiper-button-prev-custom') as HTMLElement;
    const nextButton = container.querySelector('.swiper-button-next-custom') as HTMLElement;
    const pagination = container.querySelector('.swiper-pagination-custom') as HTMLElement;

    if (prevButton === null || nextButton === null || pagination === null) {
      throw new Error('Slider elements not found');
    }

    const isPictureMode = mode === 'PICTURES';
    const isThumbMode = mode === 'THUMBS';
    const isSuggestionMode = mode === 'SUGGESTIONS';

    return {
      navigation: isThumbMode ? false : this.getNavigationOptions(nextButton, prevButton),
      pagination: isThumbMode || isSuggestionMode ? false : this.getPaginationOptions(pagination),
      slidesPerView: 'auto',
      roundLengths: isThumbMode,
      spaceBetween: isPictureMode || isSuggestionMode ? 0 : 15,
      watchSlidesProgress: isThumbMode,
      centerInsufficientSlides: isThumbMode,
      slidesOffsetBefore: isSuggestionMode ? 8 : 0,
      slidesOffsetAfter: isSuggestionMode ? 8 : 0,
      noSwiping: true,
      longSwipes: true,
      longSwipesMs: 100,
      longSwipesRatio: 0.2,
      speed: 100,
      resistance: false,
      simulateTouch: initParams.isMobile || isThumbMode,
      freeMode: isThumbMode,
      keyboard: false,
      mousewheel: true,
      grabCursor: !initParams.isMobile,
      ...(forceOptions ?? {}),
    };
  }

  // Injects navigation and pagination HTML elements into container
  private addHtmlElements(container: HTMLElement, mode: SliderMode): void {
    const isSuggestionMode = mode === 'SUGGESTIONS';
    const buttonSize = isSuggestionMode ? '-sm' : '-md';

    const leftNav = `<div role="button" class="swiper-button-prev-custom eva-3-nav-slider ${buttonSize} -eva-3-shadow-line-hover -negative -left">
                      <i class="eva-3-icon-chevron-left nav-slider-icon"></i>
                    </div>`;
    const rightNav = `<div role="button" class="swiper-button-next-custom eva-3-nav-slider ${buttonSize} -eva-3-shadow-line-hover -negative -right">
                      <i class="eva-3-icon-chevron-right nav-slider-icon"></i>
                    </div>`;

    const pagination = `<div class="swiper-pagination-custom eva-3-nav-dots -carousel -lg"></div>`;

    container.insertAdjacentHTML('beforeend', leftNav);
    container.insertAdjacentHTML('beforeend', rightNav);
    container.classList.add(`${mode.toLowerCase()}-carousel`);
    container.children[0].insertAdjacentHTML('beforeend', pagination);
  }

  // Removes navigation and/or pagination elements based on configuration
  private removeHtmlElements(container: HTMLElement, navigation: boolean, pagination: boolean): void {
    if (!navigation) {
      container.querySelector('.swiper-button-prev-custom')?.remove();
      container.querySelector('.swiper-button-next-custom')?.remove();
    }

    if (!pagination) {
      container.querySelector('.swiper-pagination-custom')?.remove();
    }
  }

  // Updates navigation visibility classes based on slider position
  private toggleNavs = (swiper: Swiper): void => {
    if (swiper.isEnd) {
      swiper.el.classList.add('no-next');
    } else {
      swiper.el.classList.remove('no-next');
    }

    if (swiper.isBeginning) {
      swiper.el.classList.add('no-prev');
    } else {
      swiper.el.classList.remove('no-prev');
    }
  };

  // Adjusts centering and offsets for mobile card sliders
  private updateCenteredSlides = (swiper: Swiper, mode: SliderMode): void => {
    const shouldCenter = initParams.isMobile && mode === 'CARDS';
    const notEdges = !swiper.isBeginning && !swiper.isEnd;

    if (shouldCenter) {
      swiper.params.centeredSlides = notEdges;
      swiper.params.slidesOffsetBefore = swiper.isBeginning ? 16 : 0;
      swiper.params.slidesOffsetAfter = swiper.isEnd ? 16 : 0;
      swiper.update();
    }
  };

  // Stores Swiper instance in WeakMap for lifecycle management
  private trackInstance(container: HTMLElement, swiper: Swiper): void {
    const existingInstances = this.swiperInstances.get(container) || [];
    existingInstances.push(swiper);
    this.swiperInstances.set(container, existingInstances);
  }
}

export const imageSliderManager = ImageSliderManager.getInstance();
